/**
 * Cart Status Constants
 * 
 * This file contains all cart status definitions to ensure consistency
 * across the application and prevent typos or inconsistencies.
 */

// Cart Instance Status Values
export const CART_STATUS = {
  // Initial states
  ACTIVE: 'active',
  ABANDONED: 'abandoned',
  
  // Payment states
  PAYMENT_FAILED: 'payment_failed',
  PAYMENT_CANCELLED_BY_USER: 'payment_cancelled_by_user',
  
  // Order processing states
  AWAITING_PACKING: 'awaiting_packing',
  PACKED_AWAITING_PICKUP: 'packed_awaiting_pickup',
  
  // Final states
  COMPLETED: 'completed',
  CANCELLED_BY_EMPLOYEE: 'cancelled_by_employee',
} as const;

// Type definition for cart status
export type CartStatus = typeof CART_STATUS[keyof typeof CART_STATUS];

// Status progression order (for validation)
export const STATUS_PROGRESSION = [
  CART_STATUS.ACTIVE,
  CART_STATUS.ABANDONED,
  CART_STATUS.PAYMENT_FAILED,
  CART_STATUS.PAYMENT_CANCELLED_BY_USER,
  CART_STATUS.CANCELLED_BY_EMPLOYEE,
  CART_STATUS.AWAITING_PACKING,
  CART_STATUS.PACKED_AWAITING_PICKUP,
  CART_STATUS.COMPLETED,
] as const;

// Helper functions for status validation
export const StatusHelpers = {
  /**
   * Check if a status is invalid for payment processing
   * (i.e., order is already in progress or completed)
   */
  isInvalidForPayment: (status: string): boolean => {
    return ([
      CART_STATUS.AWAITING_PACKING,
      CART_STATUS.PACKED_AWAITING_PICKUP,
      CART_STATUS.COMPLETED
    ] as string[]).includes(status);
  },

  /**
   * Check if a status is more advanced than awaiting_packing
   * (i.e., order has progressed beyond the initial packing stage)
   */
  isMoreAdvancedThanAwaitingPacking: (status: string): boolean => {
    return ([
      CART_STATUS.PACKED_AWAITING_PICKUP,
      CART_STATUS.COMPLETED
    ] as string[]).includes(status);
  },

  /**
   * Check if status can be updated to awaiting_packing
   * (prevents status regression)
   */
  canUpdateToAwaitingPacking: (currentStatus: string): boolean => {
    return currentStatus !== CART_STATUS.AWAITING_PACKING && 
           !StatusHelpers.isMoreAdvancedThanAwaitingPacking(currentStatus);
  },

  /**
   * Get human-readable status text
   */
  getDisplayText: (status: string): string => {
    switch (status) {
      case CART_STATUS.AWAITING_PACKING:
        return 'Awaiting Packing';
      case CART_STATUS.PACKED_AWAITING_PICKUP:
        return 'Packed - Awaiting Pickup';
      case CART_STATUS.COMPLETED:
        return 'Completed';
      case CART_STATUS.PAYMENT_FAILED:
        return 'Payment Failed';
      case CART_STATUS.CANCELLED_BY_EMPLOYEE:
      case CART_STATUS.PAYMENT_CANCELLED_BY_USER:
        return 'Cancelled';
      case CART_STATUS.ACTIVE:
        return 'Active';
      case CART_STATUS.ABANDONED:
        return 'Abandoned';
      default:
        return status || 'Unknown';
    }
  },

  /**
   * Get status priority for sorting/comparison
   */
  getPriority: (status: string): number => {
    const index = STATUS_PROGRESSION.indexOf(status as CartStatus);
    return index === -1 ? 999 : index;
  },

  /**
   * Check if status A is more advanced than status B
   */
  isMoreAdvancedThan: (statusA: string, statusB: string): boolean => {
    return StatusHelpers.getPriority(statusA) > StatusHelpers.getPriority(statusB);
  },

  /**
   * Check if a cart instance can be saved as a shopping list
   * (active carts and fulfilled/in-progress orders)
   */
  isSaveableAsList: (status: string): boolean => {
    return (STATUS_CATEGORIES.SAVEABLE_AS_LIST as readonly string[]).includes(status);
  }
};

// Status categories for easier management
export const STATUS_CATEGORIES = {
  INITIAL: [
    CART_STATUS.ACTIVE,
    CART_STATUS.ABANDONED,
  ],
  PAYMENT: [
    CART_STATUS.PAYMENT_FAILED,
    CART_STATUS.PAYMENT_CANCELLED_BY_USER,
  ],
  PROCESSING: [
    CART_STATUS.AWAITING_PACKING,
    CART_STATUS.PACKED_AWAITING_PICKUP,
  ],
  FINAL: [
    CART_STATUS.COMPLETED,
    CART_STATUS.CANCELLED_BY_EMPLOYEE,
  ],
  INVALID_FOR_PAYMENT: [
    CART_STATUS.AWAITING_PACKING,
    CART_STATUS.PACKED_AWAITING_PICKUP,
    CART_STATUS.COMPLETED,
  ],
  MORE_ADVANCED_THAN_AWAITING_PACKING: [
    CART_STATUS.PACKED_AWAITING_PICKUP,
    CART_STATUS.COMPLETED,
  ],
  SAVEABLE_AS_LIST: [
    CART_STATUS.ACTIVE,
    CART_STATUS.AWAITING_PACKING,
    CART_STATUS.PACKED_AWAITING_PICKUP,
    CART_STATUS.COMPLETED,
  ],
} as const;
